import axios from "axios";

/**
 * Content-Type to file extension, for the fallback filename.
 *
 * Content-Type is a CORS-safelisted response header, so JavaScript can always
 * read it. Content-Disposition is not - it is readable only because the API
 * lists it in `exposed_headers`. Deriving the extension from the type means a
 * download still gets a correct name if that CORS config is ever missing,
 * which is exactly the failure this file exists to prevent.
 *
 * @type {Array<[RegExp, string]>}
 */
const EXTENSIONS = [
  [/spreadsheetml\.sheet/i, "xlsx"],
  [/ms-excel/i, "xls"],
  [/\bpdf\b/i, "pdf"],
  [/\bjson\b/i, "json"],
  [/\bcsv\b|text\/plain/i, "csv"],
];

/**
 * Fetches a report export and saves it.
 *
 * These endpoints used to be plain <a href> links, which send no Authorization
 * header - so they had to sit outside the API's auth group and were readable
 * by anyone with the URL. A penetration test retrieved 1,252 customer records
 * from two of them. Going through axios means the request interceptor attaches
 * the bearer token, so the routes can be authenticated.
 *
 * @param {string} url Absolute URL of the export
 * @param {(message: string) => void} [onError] Receives a message to show the user
 * @returns {Promise<void>}
 */
export default async function downloadExport(url, onError) {
  if (!url) {
    return;
  }

  try {
    const response = await axios.get(url, { responseType: "blob" });
    const headers = response.headers || {};
    const filename =
      filenameFromDisposition(headers["content-disposition"]) ||
      fallbackFilename(url, headers["content-type"]);

    save(new Blob([response.data]), filename);
  } catch (error) {
    // A failed export used to navigate the tab to an error page. Report it and
    // leave the user where they were, with their form still filled in.
    if (onError) {
      onError(messageFor(error));
    }
  }
}

/**
 * Saves a file response from a request made elsewhere (a POST that returns
 * a file, which downloadExport's GET cannot make). The request must have used
 * `responseType: "blob"`.
 *
 * @param {import("axios").AxiosResponse} response The blob response
 * @param {string} fallbackName Path-like name used when the server gave none, e.g. "conectia-reply"
 * @returns {void}
 */
export function saveDownloadResponse(response, fallbackName) {
  const headers = response?.headers || {};
  const filename =
    filenameFromDisposition(headers["content-disposition"]) ||
    fallbackFilename(fallbackName, headers["content-type"]);

  save(new Blob([response.data]), filename);
}

/**
 * Filename the server asked for, or null when it did not say.
 *
 * @param {string} [disposition] Raw Content-Disposition header
 * @returns {string|null}
 */
function filenameFromDisposition(disposition) {
  if (!disposition) {
    return null;
  }

  // filename* (RFC 5987) wins where present, because it is the form that
  // survives non-ASCII. Read naively, `filename*=UTF-8''report.csv` also
  // matches the basic pattern and yields a file called "*=UTF-8".
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];

  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // Malformed percent-encoding: fall through to the basic parameter.
    }
  }

  return disposition.match(/filename="?([^"';]+)"?/i)?.[1] || null;
}

/**
 * Last-resort filename, from the export's path and the type actually returned.
 *
 * The extension MUST come from the response rather than being assumed. Every
 * one of these endpoints returns CSV - Excel::download picks its writer from
 * the extension it is given, and each is given a .csv name - so hard-coding
 * .xlsx here produced CSV files that Excel refused to open.
 *
 * @param {string} url
 * @param {string} [contentType]
 * @returns {string}
 */
function fallbackFilename(url, contentType) {
  const path = url.split("?")[0].split("/").filter(Boolean).pop() || "export";
  const match = EXTENSIONS.find(([pattern]) => pattern.test(contentType || ""));

  return `${path}.${match ? match[1] : "csv"}`;
}

/**
 * What to tell the user, distinguished by cause.
 *
 * These exports build the whole file inside the web request, and the reports
 * page has a long-standing 60 second gateway timeout on the larger ones. That
 * reads very differently from a permission problem and should not be reported
 * with the same words.
 *
 * @param {unknown} error
 * @returns {string}
 */
function messageFor(error) {
  const status = error?.response?.status;

  if (status === 504 || status === 408 || error?.code === "ECONNABORTED") {
    return "The export took too long and timed out. Try a shorter date range.";
  }

  if (status === 401 || status === 403) {
    return "You do not have permission to run this export.";
  }

  return "Export failed, please try again.";
}

/**
 * Saves a blob under the given name.
 *
 * @param {Blob} blob
 * @param {string} filename
 * @returns {void}
 */
function save(blob, filename) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = objectUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Revoking immediately can cancel the download in Safari.
  setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
}
